'use client';
import React, { useState, useEffect } from 'react';
import { ShippingAddress as Shared, type ShippingAddressValue } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { CART_UPDATED_EVENT } from '@/lib/hooks/useCart';
import { checkoutSelections } from './checkout-state';

const SharedRender = (Shared as any).render;

// Module-level state for the "Same as billing" checkbox. Persists across
// any wrapper re-mounts (which can happen when the Puck content tree
// re-renders, e.g. on cart refetch) so the user's choice doesn't get
// silently reset.
const sameAsBillingState: { value: boolean } = {
  value: true, // matches shared component default
};

const emptyAddress: ShippingAddressValue = {
  email: '',
  first_name: '',
  last_name: '',
  company: '',
  address_1: '',
  address_2: '',
  city: '',
  province: '',
  postal_code: '',
  country_code: '',
  phone: '',
};

/**
 * Storefront ShippingAddress — maintains the form's controlled state,
 * pre-fills from `cart.shipping_address` (or a selected saved address),
 * and POSTs to /api/cart/address on "Continue to Shipping Method".
 *
 * Countries + saved addresses come from Puck context. The country
 * select is restricted to the cart's region countries.
 */
function ShippingAddressImpl(props: any) {
  const { context } = usePuckContext() as any;
  const cart = context?.cart;
  const customer = context?.customer;
  // Use the first country in the region as a sensible default when the
  // cart has no address yet. Without this, the country select is
  // controlled with value="" and the browser displays the first option
  // visually — but `formData.country_code` stays `""` and the user
  // thinks "I didn't touch it, so it must be selected", then the
  // validation fails on country_code. Initialising to the first country
  // makes the first option actually selected by value.
  const firstCountryCode = cart?.region?.countries?.[0]?.iso_2 ?? '';
  const [formData, setFormData] = useState<ShippingAddressValue>(() => ({
    ...emptyAddress,
    email: cart?.email || customer?.email || '',
    ...(cart?.shipping_address ?? {}),
    country_code: cart?.shipping_address?.country_code || firstCountryCode,
  }));
  const [sameAsBilling, setSameAsBillingLocal] = useState(sameAsBillingState.value);
  const setSameAsBilling = (v: boolean) => {
    sameAsBillingState.value = v;
    setSameAsBillingLocal(v);
  };
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If cart.shipping_address updates from the server (e.g. after a refetch),
  // re-sync the form to the server values (only if the user hasn't started
  // editing — we don't want to clobber their in-progress input).
  useEffect(() => {
    if (!cart?.shipping_address) return;
    setFormData((prev) => ({
      ...prev,
      email: prev.email || cart.email || customer?.email || '',
      first_name: prev.first_name || cart.shipping_address.first_name || '',
      last_name: prev.last_name || cart.shipping_address.last_name || '',
      address_1: prev.address_1 || cart.shipping_address.address_1 || '',
      city: prev.city || cart.shipping_address.city || '',
      postal_code: prev.postal_code || cart.shipping_address.postal_code || '',
      country_code: prev.country_code || cart.shipping_address.country_code || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id]);

  if (!cart?.region?.countries?.length) {
    return <SharedRender {...props} countries={undefined} />;
  }

  const countries = cart.region.countries
    .map((c: any) => ({ code: c.iso_2, name: c.name }))
    .filter((c: any) => c.code)
    .sort((a: any, b: any) => a.name.localeCompare(b.name));

  // Filter saved addresses by region countries
  const regionCountryCodes = new Set(countries.map((c: any) => c.code));
  const savedAddresses = (customer?.addresses ?? [])
    .filter((a: any) => a.country_code && regionCountryCodes.has(a.country_code));

  const handleSelectSaved = (a: ShippingAddressValue) => {
    setFormData((prev) => ({ ...prev, ...a }));
  };

  const handleContinue = async () => {
    if (isSaving) return;
    setError(null);
    // DEBUG: log the formData so we can see exactly which required field is empty
    console.log('[ShippingAddress] handleContinue - formData:', JSON.stringify(formData, null, 2));
    // Minimal validation
    if (!formData.email || !formData.first_name || !formData.last_name ||
        !formData.address_1 || !formData.city || !formData.postal_code || !formData.country_code) {
      // Build a specific list of missing fields so the user (and the
      // developer) can see exactly which input is the problem.
      const missing: string[] = [];
      if (!formData.email) missing.push('email');
      if (!formData.first_name) missing.push('first_name');
      if (!formData.last_name) missing.push('last_name');
      if (!formData.address_1) missing.push('address_1');
      if (!formData.city) missing.push('city');
      if (!formData.postal_code) missing.push('postal_code');
      if (!formData.country_code) missing.push('country_code');
      console.warn('[ShippingAddress] Missing required fields:', missing);
      setError(`Please fill in all required fields (missing: ${missing.join(', ')}).`);
      return;
    }
    setIsSaving(true);
    try {
      const res = await fetch('/api/cart/address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          shippingAddress: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            company: formData.company,
            address_1: formData.address_1,
            address_2: formData.address_2,
            city: formData.city,
            province: formData.province,
            postal_code: formData.postal_code,
            country_code: formData.country_code?.toLowerCase(),
            phone: formData.phone,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to save address');
      }
      // Persist for OrderReview (which reads from the stale Puck context)
      // AND so the next section (ShippingMethod) becomes visible
      // immediately via the step-gating fallback (the cart refetch may
      // take a moment to update Puck context).
      checkoutSelections.address = { ...formData };
      window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
      // No scroll: the next section (Delivery) is gated on cart state
      // and appears right below the form once the cart refetches.
    } catch (e: any) {
      setError(e?.message || 'Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };

  // Hide the "Continue to Shipping Method" button once the address is
  // saved. The user has moved past step 1; the button no longer makes
  // sense. `checkoutSelections.address` is set synchronously on save
  // (the cart refetch is async), so we OR it with the cart's own
  // shipping_address check to handle the "saved but cart not refetched
  // yet" window.
  const addressDone = !!checkoutSelections.address ||
    !!cart?.shipping_address?.first_name;
  const showContinueButton = !addressDone;

  return (
    <div>
      <SharedRender
        {...props}
        countries={countries}
        savedAddresses={savedAddresses}
        formData={formData}
        onFormChange={setFormData}
        onSelectSavedAddress={handleSelectSaved}
        onContinue={handleContinue}
        showContinueButton={showContinueButton}
        isLoading={isSaving}
        loadingText="Saving address…"
        sameAsBilling={sameAsBilling}
        onSameAsBillingChange={setSameAsBilling}
      />
      {error && (
        <div className="mt-3 text-sm text-red-600 px-1">{error}</div>
      )}
    </div>
  );
}

export const ShippingAddress: any = {
  ...Shared,
  render: (props: any) => <ShippingAddressImpl {...props} />,
};
export default ShippingAddress;
